import { ReactComponent as LockIcon } from '@vkontakte/icons/src/svg/16/lock_outline_16.svg';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SharingNewDropdown } from 'reactApp/components/SharingNewBiz/Dropdown/SharingNewDropdown';
import { getDomainAccessOptions, QA_VALUE, TOOLTIP_OFFSET } from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { sendGa } from 'reactApp/components/SharingNewBiz/SharingNew.helpers';
import { DomainAccess } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import styles from 'reactApp/components/SharingNewBiz/Weblink/SharingNewWeblink.css';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { publishRequest, toggleWeblinkDomesticRequest } from 'reactApp/modules/modifying/modifying.actions';
import { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { renderUnpublishWeblinkDialog } from '../Weblink/SharingNewWeblink.helpers';

interface Props {
    isWeblinkOpened: boolean;
    onActionClick: () => void;
    item?: PublishItem;
    domain: string;
}

export const DomesticDropdown: React.FC<Props> = ({ item, isWeblinkOpened, onActionClick, domain }) => {
    const [selectedId, setSelectedId] = useState<DomainAccess | undefined>(undefined);
    const dispatch = useDispatch();
    const isPhone = EnvironmentSelectors.isPhone();
    const domainAccess = item?.weblinkDomestic ? DomainAccess.DOMESTIC : DomainAccess.EXTERNAL;
    const checked = !isWeblinkOpened ? DomainAccess.OFF : domainAccess;
    const isFolder = item?.isFolder;

    const domainAccessOptions = useMemo(() => getDomainAccessOptions(domain || ''), [domain]);

    const renderDomesticDropdown = useCallback(
        (onClick: void): ReactElement => {
            const list = Object.values(domainAccessOptions).map((item) => ({
                id: item.id,
                text: item.text,
                check: item.id === checked,
                qaValue: item.id,
            }));

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
        },
        [checked, domainAccessOptions]
    );

    const onToggleDomestic = useCallback(
        (id: DomainAccess): void => {
            if (id !== domainAccess && item) {
                sendGa('domestic');
                sendPaymentGa({
                    eventCategory: ECategoryGa.public,
                    action: 'edit-access',
                    type_for: id,
                    public_id: item.weblink,
                });
                dispatch(toggleWeblinkDomesticRequest({ item }));
            }
        },
        [domainAccess, item, dispatch]
    );

    const handleUpdateDomestic = useCallback(
        (id: DomainAccess): void => {
            if (id === DomainAccess.OFF && isWeblinkOpened) {
                renderUnpublishWeblinkDialog({ onActionClick });
                return;
            }

            if (!isWeblinkOpened && item) {
                sendGa('publish');

                dispatch(publishRequest({ item }));
                setSelectedId(id);
                return;
            }

            onToggleDomestic(id);
        },
        [isWeblinkOpened, onToggleDomestic, onActionClick, dispatch, item]
    );

    // данный эффект нужен, т.к. при выключенном веблинке, нужно сначала его включить
    useEffect(() => {
        if (selectedId && item?.weblink) {
            onToggleDomestic(selectedId);
            setSelectedId(undefined);
        }
    }, [selectedId, item, onToggleDomestic]);

    const renderDomestic = useCallback((): ReactElement => {
        const item = domainAccessOptions[checked];

        return (
            <div className={styles.rightsItem} data-qa-item={QA_VALUE.domesticItem}>
                <span className={styles.icon}>
                    <LockIcon />
                </span>
                <span className={styles.rightsTitle}>{isFolder ? 'Папка доступна по ссылке' : 'Файл доступен по ссылке'}</span>
                <div className={styles.dropdown}>
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={item.id}
                        as={SharingNewDropdown}
                        value={item.text}
                        content={renderDomesticDropdown}
                        onClickItem={handleUpdateDomestic}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.domesticDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                    />
                </div>
            </div>
        );
    }, [checked, handleUpdateDomestic, isFolder, isPhone, renderDomesticDropdown, domainAccessOptions]);

    return renderDomestic();
};
